import itiriri from 'itiriri';
import {
  CreateSyringeDriverRunReq,
  HSDoseRound,
  WardDto,
  OperateSyringeDriverRunReq,
  SyringeDriverRunActivityDto,
  SyringeDriverRunAdministeredDrugDto,
  SyringeDriverRunDto
} from 'server-openapi';
import {PersistentQueue, QueueEntry, QueueOperationException} from '../core/queue/PersistentQueue';
import {Entry, IStorage} from '../core/storage/Contract';
import { SyncStreamAPI } from './api';
import {IFacilityGroupSyncService } from './SyncCenter';
import { v4 as uuidv4 } from 'uuid';
import { SyncUtils, ChangeNumberWithDateString } from './utils/SyncUtils';
import { Logger } from '../core/logger/logger';
import { DateUtils } from '../core/utils/dateUtils';
import { addDays, startOfDay, subDays } from "date-fns";
import {MemoryCache} from "../core/storage/MemoryCache";
import {mergeSyringeDriverRuns} from "./utils/SyringeDriverRunUtils";
import {RoundOp} from "./SyncRounds";

export type SyringeDriverRunOp = CreateSyringeDriverRunOp | OperateSyringeDriverRunOp;

export interface OperateSyringeDriverRunBaseOp extends QueueEntry {
  type: string;
  syringeDriverRunClinicalSystemId?: string;
  syringeDriverRunActivityClinicalSystemId?: string;
  subjectId?: string;
}

export interface CreateSyringeDriverRunOp extends OperateSyringeDriverRunBaseOp {
  type: 'syringe-driver-run-create';
  patientId: number;
  doseRoundClinicalSystemId?: string;
  administeredDrugClinicalSystemIds?: string[];
  administeredAdHocDrugClinicalSystemIds?: string[];
}
export interface OperateSyringeDriverRunOp extends OperateSyringeDriverRunBaseOp {
  type: 'syringe-driver-run-operate';
}

const logger = new Logger('SyncSyringeDriverRuns');

export class SyncSyringeDriverRuns implements IFacilityGroupSyncService {
    private syncToDate: Date;

    get name(): string {
    return 'SyncSyringeDriverRuns';
  }
  private async isStale(p: SyringeDriverRunDto): Promise<boolean> {
    return (!!(!p.active && p.clientUpdatedAt && p.clientUpdatedAt < DateUtils.fromDate(subDays(startOfDay(new Date()), 14))));
  }

  private async isFacilityGroup(p: SyringeDriverRunDto, facilityGroupId: string): Promise<boolean> {
    if (!p.wardId) {
      return false;
    }
    const facilityGroup = await this.facilitiesStore.get(p.wardId!.toString());
    return facilityGroup?.facilityGroupId?.toString() == facilityGroupId;
  }

  constructor(
    private api: SyncStreamAPI,
    private storage: MemoryCache<SyringeDriverRunDto>,
    private facilitiesStore: IStorage<WardDto>,
    private latestChangeNumbers: IStorage<number | undefined>,
    // private latestChangeNumbersWithDate: IStorage<ChangeNumberWithDateString>,
    private roundsStore: IStorage<HSDoseRound>,
    private queue: PersistentQueue<SyringeDriverRunOp>,
    private roundQueue: PersistentQueue<RoundOp>,
  ) {
      this.syncToDate = startOfDay(addDays(new Date(), 1));
  }
  async load(facilityGroupId: string): Promise<void> {
    await this.storage.reset(async (r) => {
          return await this.isFacilityGroup(r, facilityGroupId);
    });
  }

  async syncDown(facilityGroupId?: string) {
    const facilitiesToSync = await SyncUtils.getFacilitiesForGroup(facilityGroupId, this.facilitiesStore);

    const changeNumber = await SyncUtils.getChangeNumberForFacilities(
      facilitiesToSync.map((x) => x.hsId!),
      this.latestChangeNumbers,
    );

    // const changeNumberWithDate = await SyncUtils.getChangeNumberWithDateForFacilities(
    //       facilitiesToSync.map((x) => x.hsId!),
    //       this.latestChangeNumbersWithDate,
    // );
    //
    // console.log(`changeNumberWithDate: ${changeNumberWithDate}`);

    const syringeDriverRunData = await this.syncFacilityDown(
      facilityGroupId!,
      changeNumber,
    );

    const mergedResult = await Promise.all(syringeDriverRunData
        .map(async (run) => {
          const key = this.storage.get_key!(run);
          const existing = await this.storage.get(key);
          if (existing) {
            return mergeSyringeDriverRuns(run, existing);
          }
          return run;
        }));
    await this.storage.setMany(mergedResult.map((run) => {
        const key = this.storage.get_key!(run);
        return {
          key: key!,
          value: {
            ...run,
            clinicalSystemId: key,
          },
        };
      }),
    );
        

    await SyncUtils.setChangeNumberForFacilities(
        facilitiesToSync.map((x) => x.hsId!),
        this.latestChangeNumbers,
        syringeDriverRunData
    );

    // await SyncUtils.setChangeNumberWithDateForFacilities(
    //     facilitiesToSync.map((x) => x.hsId!),
    //     this.latestChangeNumbersWithDate,
    //     this.syncToDate,
    //     syringeDriverRunData
    // );
  }

  async syncFacilityDown(facilityGroupId: string, changeNumber: number): Promise<SyringeDriverRunDto[]> {
    const pageSize = 200;
    //changeNumber = isFinite(changeNumber) ? changeNumber : 0;
    const syringeDriverRuns =
      await this.api.syringeDriverRuns.syringeDriverRunsListSyringeDriverRuns(
        changeNumber,
        parseInt(facilityGroupId),
        pageSize
      );

    if (syringeDriverRuns.data.length === pageSize) {
      return [
        ...syringeDriverRuns.data,
        ...(await this.syncFacilityDown(facilityGroupId, SyncUtils.getLatestChangeNumberForV2Data(syringeDriverRuns.data)!)),
      ];
    }
    return syringeDriverRuns.data;
  }

  enqueue = {
    createSyringeDriverRun: async (startActivity: SyringeDriverRunActivityDto, subjectId: string, wardId: number,  patientId: number, administeredDrugs: SyringeDriverRunAdministeredDrugDto[], doseRoundClinicalSystemId?: string): Promise<CreateSyringeDriverRunOp> => {
      // The clinical system id of the syringe driver runs is the same as that of its start activity.
      startActivity.clinicalSystemId ??= uuidv4();
      startActivity.syringeDriverRunClinicalSystemId = startActivity.clinicalSystemId;
      const result: CreateSyringeDriverRunOp = {
        type: "syringe-driver-run-create",
        state: "Pending", 
        patientId: patientId, 
        createdAt: startActivity.clientCreatedAt,
        syringeDriverRunActivityClinicalSystemId: startActivity.clinicalSystemId ?? undefined, 
        syringeDriverRunClinicalSystemId: startActivity.clinicalSystemId ?? undefined,
        doseRoundClinicalSystemId: doseRoundClinicalSystemId ?? undefined, 
        subjectId: subjectId, 
        administeredAdHocDrugClinicalSystemIds: administeredDrugs.filter(x => !!x.administeredAdHocDrugClinicalSystemId).map(x => x.administeredAdHocDrugClinicalSystemId!) ?? [],
        administeredDrugClinicalSystemIds: administeredDrugs.filter(x => !!x.administeredDrugClinicalSystemId).map(x => x.administeredDrugClinicalSystemId!) ?? [],
      }
      const run: SyringeDriverRunDto  = {
        active: true, 
        statusCheck: {...startActivity.statusCheck},
        administeredDrugs: administeredDrugs, 
        startedAt: startActivity.clientCreatedAt,
        clientCreatedAt: startActivity.clientCreatedAt,
        clientUpdatedAt: startActivity.clientCreatedAt,
        scheduledEnd: startActivity.scheduledEnd ?? undefined, 
        clinicalSystemId: startActivity.clinicalSystemId,
        syringeDriverIdentifier: startActivity.syringeDriverIdentifier,
        comment: startActivity.comment, 
        diluent: startActivity.diluent, 
        line: {...startActivity.line},
        clientUser: {...startActivity.clientUser}, 
        activities: [
            startActivity
        ], 
        patientId: patientId,  
        site: startActivity.site, 
        infusion: startActivity.infusion, 
        status: "Running", 
        wardId: wardId
      };
      
      await this.storage.set(startActivity.clinicalSystemId!, run);
      await this.queue.unshift(result);
      return result;
    },
    operateSyringeDriverRun: async (run: SyringeDriverRunDto, operation: SyringeDriverRunActivityDto,  subjectId: string): Promise<OperateSyringeDriverRunOp> => {
      operation.clinicalSystemId ??= uuidv4();
      const result: OperateSyringeDriverRunOp = {
        type: "syringe-driver-run-operate",
        state: "Pending",
        createdAt: run.startedAt,
        syringeDriverRunClinicalSystemId: run.clinicalSystemId ?? undefined,
        syringeDriverRunActivityClinicalSystemId: operation.clinicalSystemId, 
        subjectId: subjectId,
      };
      // merge the operation into the syringe driver run object.
      const newRun: SyringeDriverRunDto = {
        activities: [operation]
      };
      const mergedEntry = mergeSyringeDriverRuns(run, newRun);
      await this.storage.set(run.clinicalSystemId!, mergedEntry);
      await this.queue.unshift(result);
      return result;
    },
  };

  
  //eslint-disable-next-line sonarjs/cognitive-complexity, max-lines-per-function
  async syncUp() {
    const entries = new Map<string, SyringeDriverRunDto>();
    for await (const operation of this.queue.iterate()) {
      try {
        switch (operation.value.type) {
        case 'syringe-driver-run-create': {
            const storedRun = (await this.storage.get(operation.value.syringeDriverRunClinicalSystemId!)) ?? entries.get(operation.value.syringeDriverRunClinicalSystemId!);
            if (!storedRun) {
              throw new QueueOperationException("UnrecoverableError", `Syringe driver run '${operation.value.syringeDriverRunClinicalSystemId}' not found when attempting to upload syringe driver run`);
            }
            
            const startActivity = storedRun?.activities?.find(x => x.clinicalSystemId == operation.value.syringeDriverRunActivityClinicalSystemId);
            if (!startActivity) {
              throw new QueueOperationException("UnrecoverableError", `Start activity '${operation.value.syringeDriverRunActivityClinicalSystemId}' not found when creating syringe driver run`);
            }
            let round : HSDoseRound | undefined;
            if (operation.value.doseRoundClinicalSystemId) {
              round = await this.roundsStore.get(operation.value.doseRoundClinicalSystemId);
              let found = false;
              if (round === undefined) {
                for await (const delivery of this.roundQueue.iterate()) {
                  if (delivery.value.type === "round-create") {
                    const createRoundOp = delivery.value;
                    if (createRoundOp.clinicalSystemId === operation.value.doseRoundClinicalSystemId) {
                      found = true;
                      break;
                    }
                  }
                }
                if (!found) {
                  throw new QueueOperationException("UnrecoverableError", `Round '${operation.value.doseRoundClinicalSystemId}' not found when creating syringe driver run activity`);
                }
                else {
                  throw new QueueOperationException("PendingParent", `Dose round ${operation.value.doseRoundClinicalSystemId} has not been uploaded yet`);
                }
              }
              else if (!round.hsId || round.hsId <= 0) {
                // Not uploaded yet.
                throw new QueueOperationException("PendingParent", `Dose round ${operation.value.doseRoundClinicalSystemId} has not been uploaded yet`);
              }

            }

            const request : CreateSyringeDriverRunReq = {
              subjectId: operation.value.subjectId ?? "", 
              administeredDrugs: [], 
              patientId: operation.value.patientId,
              startActivity: startActivity
            };
            const administeredDrugs: SyringeDriverRunAdministeredDrugDto[] = [];
            for (const adhoc of operation.value.administeredAdHocDrugClinicalSystemIds ?? []) {
              if (!!adhoc) {
                const adHocDrug = round!.administeredDoses?.flatMap(x => x.administeredAdHocDrugs).find(x => x?.clinicalSystemId == adhoc);
                let found = false;
                if (!adHocDrug) {
                  for await (const delivery of this.roundQueue.iterate()) {
                    if (delivery.value.type === "adhoc-drug-create") {
                      const createOp = delivery.value;
                      if (createOp.clinicalSystemId === adhoc) {
                        found = true;
                        break;
                      }
                    }
                  }
                  if (!found) {
                    throw new QueueOperationException("UnrecoverableError", `Unable to find administered ad-hoc drug with clinical system id: ${adhoc} for dose round ${round!.hsId}`);
                  }
                  else {
                    throw new QueueOperationException("PendingParent", `Administered ad-hoc drug ${adhoc} has not been uploaded yet`);
                  }
                }
                else {
                  administeredDrugs.push({
                    administeredAdHocDrugClinicalSystemId: adHocDrug?.clinicalSystemId,
                    administeredAdHocDrugId: adHocDrug!.hsId,
                    confirmationUser: {
                      hsId: adHocDrug.confirmationUserId ?? undefined,
                    }
                  });
                }
              }
            }
            for (const administeredDrugId of operation.value.administeredDrugClinicalSystemIds ?? []) {
              if (administeredDrugId !== "") {
                // ignore.
                const adrug = round!.administeredDoses?.flatMap(x => x.administeredDrugs).find(x => x?.clinicalSystemId == administeredDrugId);
                let found = false;
                if (adrug === undefined) {
                  for await (const delivery of this.roundQueue.iterate()) {
                    if (delivery.value.type === "drug-create") {
                      const createOp = delivery.value;
                      if (createOp.clinicalSystemId === administeredDrugId) {
                        found = true;
                        break;
                      }
                    }
                  }
                  if (!found) {
                    throw new QueueOperationException("UnrecoverableError", `Unable to find administered drug with clinical system id: ${administeredDrugId} for dose round ${round!.hsId}`);
                  } else {
                    throw new QueueOperationException("PendingParent", `Administered drug ${adrug} has not been uploaded yet`);
                  }
                }
                administeredDrugs.push({
                  administeredDrugClinicalSystemId: administeredDrugId, 
                  administeredDrugId: adrug!.hsId,
                  confirmationUser: {
                    hsId: adrug!.confirmationUserId ?? undefined,
                  }
                });
              }
            }
            request.administeredDrugs = administeredDrugs;
            if (administeredDrugs.length == 0) {
              throw new QueueOperationException("UnrecoverableError", `Syringe driver run attempted to be created with no drugs`);
            }

            const response = await this.api.syringeDriverRuns.syringeDriverRunsCreateSyringeDriverRun(request);
            entries.set(operation.value.syringeDriverRunClinicalSystemId!, mergeSyringeDriverRuns(response.data, storedRun!))
          }
          break;
          case 'syringe-driver-run-operate': {
            const storedRun =
                (await this.storage.get(operation.value.syringeDriverRunClinicalSystemId!)) ??
                entries.get(operation.value.syringeDriverRunClinicalSystemId!);
            if (!storedRun?.hsId || storedRun.hsId <= 0) {
              throw new QueueOperationException("PendingParent", `Syringe driver run  ${storedRun?.clinicalSystemId} has not been uploaded yet`);
            }
            const activity = storedRun?.activities?.find(x => x.clinicalSystemId == operation.value.syringeDriverRunActivityClinicalSystemId);
            if (!activity) {
              // TODO:  Make this unrecoverable
              throw new Error(`Activity '${operation.value.syringeDriverRunActivityClinicalSystemId}' unable to be found for syringe driver run`);
            }
            const operateRequest : OperateSyringeDriverRunReq = {
              subjectId: operation.value.subjectId ?? "",
              activity: activity,  
              syringeDriverRunId: storedRun?.hsId, 
            };
            const response = await this.api.syringeDriverRuns.syringeDriverRunsOperateSyringeDriverRun(operateRequest);
            entries.set(operation.value.syringeDriverRunClinicalSystemId!, mergeSyringeDriverRuns(response.data, storedRun!))
            break;
          }
        }
        await operation.complete();
      } catch (error) {
        await operation.handleException(logger, error, operation);
      }
    }
    await this.storage.setMany(
      itiriri(entries.entries())
        .map((entry) => ({ key: entry[0], value: entry[1] }))
        .toArray(),
    );
  }

  async clear() {
    await this.storage.clear();
    await this.latestChangeNumbers.clear();
    // await this.latestChangeNumbersWithDate.clear();
    await this.queue.clear();
  }

  hasQueuedData() {
    return this.queue.hasPendingMessages();
  }

  queuedDataCount() {
    return this.queue.pendingMessagesCount();
  }

  isAllowed(canUserAccessMedication: boolean): boolean {
    // Only if you can view a round.
    return canUserAccessMedication;
  }
  async archive(deletedPatientIds: string[]): Promise<void> {
    const keysToDelete: string[] = [];
    for (const [k, v] of (await this.storage.all()).entries()) {
      if (await this.isStale(v) || deletedPatientIds.some(x => x === (v.patientId?.toString() ?? "-1"))) {
        keysToDelete.push(k)
      }
    }
    await this.storage.deleteMany(keysToDelete);
  }
  setEncryptionVersion(version: number): void {
    this.storage.compressOnSave = (version > 1);
  }
  async rewrite(): Promise<void> {
    const entries: Entry<SyringeDriverRunDto>[] = [...(await this.storage.all())].map((keyValueArray) => {
      return {
        key: keyValueArray[0],
        value: keyValueArray[1]
      };
    });

    return await this.storage.setMany(entries);
  }}
